import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, ReplaySubject, catchError, of } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { RuntimeConfigService } from "../../../../services/runtime-config.service";
import { GuardianStudent } from "../../profiles/models/guardian-student.model";

@Injectable({
  providedIn: 'root'
})

export class UserImageService {

  private apiUrl: string;
  private userImageCache = new Map<number, BehaviorSubject<UserImageResponse | null>>();

  /**
   * Holds a ReplaySubject for each userId that has been requested in the current batch window
   * but not yet fulfilled. Once the HTTP batch request completes, each subject emits its result
   * and completes, then is removed from this map.
   */
  private pendingBatch = new Map<number, ReplaySubject<UserImageResponse | null>>();

  /**
   * Handle returned by setTimeout() for the pending batch flush.
   * Non-null means a flush is already scheduled — prevents scheduling duplicates.
   */
  private batchFlushTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * userIds whose cache was just invalidated because their image changed. The next batch that
   * includes any of them reads the primary database instead of the read-only replica, so it sees
   * the new photo/avatar rather than a lagging copy. Every other batch opts in to the replica.
   */
  private mustReadPrimary = new Set<number>();

  constructor(
    private http: HttpClient,
    private runtimeConfig: RuntimeConfigService
  ) {
    this.apiUrl = this.runtimeConfig.currentConfig.apiUrl;

    // Subscribe to config changes to update apiUrl
    this.runtimeConfig.config$.subscribe(config => {
      this.apiUrl = config.apiUrl;
    });
  }

  /**
   * Returns an Observable that emits the image and avatar data for the given user.
   *
   * ## Batching behavior
   * Rather than firing one HTTP request per call, this method implements a collect-then-flush
   * pattern to handle pages that render many user-image components at once (e.g. grids):
   *
   * 1. The first call for a given userId in a render cycle creates a ReplaySubject and schedules
   *    a flush via setTimeout(0).
   * 2. Subsequent calls within the same Angular change detection cycle find the existing subject
   *    and return it — no duplicate work, no duplicate HTTP requests.
   * 3. After Angular's synchronous cycle completes, setTimeout(0) fires. By then all userIds for
   *    this render are queued. One POST request is made for all of them, and each result is routed
   *    back to its corresponding subject.
   *
   * ## Why ReplaySubject(1) and not Subject
   * ReplaySubject(1) buffers the last emitted value. If a component subscribes after the batch
   * result arrives (rare edge case with async rendering), it still receives the value rather than
   * missing it silently.
   *
   * ## Cache hits
   * If data for this userId is already in the session cache, it is returned synchronously via
   * of() so the component's ServiceStatus reaches `loaded` immediately without any HTTP.
   */
  getUserImageAndAvatar(userId: number): Observable<UserImageResponse | null> {
    // Fast path: return cached value immediately if available.
    // The component pre-checks the cache for an optimistic render, then calls here to get the
    // observable — returning of(cached) ensures ServiceStatus reaches `loaded` without a new request.
    const cached = this.userImageCache.get(userId);
    if (cached) return of(cached.value);

    // If a subject already exists for this userId, another component already queued it in this
    // batch window. Return the same observable so both components share the single HTTP request.
    if (this.pendingBatch.has(userId)) {
      return this.pendingBatch.get(userId)!.asObservable();
    }

    // First request for this userId in this batch window.
    // Create a placeholder subject and add this userId to the pending queue.
    const subject = new ReplaySubject<UserImageResponse | null>(1);
    this.pendingBatch.set(userId, subject);

    // Schedule the flush only once per batch window. setTimeout(0) defers until after Angular's
    // synchronous change detection cycle finishes, by which point all components that rendered
    // together will have registered their userIds.
    if (this.batchFlushTimer === null) {
      this.batchFlushTimer = setTimeout(() => this.flushBatch(), 0);
    }

    return subject.asObservable();
  }

  /**
   * Fires the batch HTTP request for all userIds collected since the last flush, then routes
   * each result back to its corresponding ReplaySubject.
   *
   * Called automatically by the setTimeout(0) scheduled in getUserImageAndAvatar().
   */
  private flushBatch(): void {
    // Swap out the pending maps before the async HTTP call so that any getUserImageAndAvatar()
    // calls that arrive while the request is in-flight start a fresh batch.
    const batch = this.pendingBatch;
    this.pendingBatch = new Map();
    this.batchFlushTimer = null;

    const userIds = Array.from(batch.keys());
    // Filter out any null/undefined userIds to avoid sending bad requests to the API
    const filteredUserIds = userIds.filter(id => id != null);
    if (filteredUserIds.length === 0) {
      // Nothing to fetch, but the null/undefined-id callers still await a result
      for (const subject of batch.values()) {
        subject.next(null);
        subject.complete();
      }
      return;
    }

    const needsPrimary = filteredUserIds.some(id => this.mustReadPrimary.has(id));
    filteredUserIds.forEach(id => this.mustReadPrimary.delete(id));

    this.http.post<UserImageResponse[]>(
      `${this.apiUrl}/UserImage/GetUserImagesAndAvatarsAsync`,
      filteredUserIds,
      needsPrimary ? {} : { params: { replica: 'true' } }
    ).pipe(
      catchError(error => {
        console.error('[UserImageService] Error fetching batch user images:', error);
        return of(null);
      })
    ).subscribe(results => {
      for (const [userId, subject] of batch.entries()) {
        const result = results?.find(r => r.UserId === userId) ?? null;
        if (result) {
          // Populate the session cache so future renders for this userId are instant
          this.userImageCache.set(userId, new BehaviorSubject<UserImageResponse | null>(result));
        }
        subject.next(result);
        subject.complete();
      }
    });
  }

  loadCachedUserImageResponse(userId: number): UserImageResponse | null {
    const cacheSubject = this.userImageCache.get(userId);
    return cacheSubject ? cacheSubject.value : null;
  }

  /**
   * Drops any cached image data for the user so the next getUserImageAndAvatar() re-fetches
   * from the server instead of returning the stale cached value. Call this after the user's
   * image changes (e.g. uploading a new profile photo). Any in-memory blob URL is revoked.
   */
  invalidateUserImageCache(userId: number): void {
    const cached = this.userImageCache.get(userId);
    const url = cached?.value?.ImageUrl;
    if (url && url.startsWith('blob:')) {
      try { URL.revokeObjectURL(url); } catch { /* ignore */ }
    }
    this.userImageCache.delete(userId);
    this.mustReadPrimary.add(userId);
  }

  loadImageUrlFromBlob(userImageResponse: UserImageResponse): string {
    // If SecureUrl is available (Azure Blob Storage), use it directly
    if (userImageResponse?.SecureUrl) {
      userImageResponse.ImageUrl = userImageResponse.SecureUrl;
      return userImageResponse.ImageUrl;
    }

    if (userImageResponse?.ProfileImageContent?.FileContents) {
      // Convert the base64 ProfileImageContent to a blob URL
      try {
        // The FileContents is base64 encoded, so decode it first
        const base64Data = userImageResponse.ProfileImageContent.FileContents;
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const profileImageBlob = new Blob([byteArray], { type: 'image/png' });
        userImageResponse.ImageUrl = URL.createObjectURL(profileImageBlob);
      } catch (error) {
        userImageResponse.ImageUrl = "";
        console.error("Error converting profile image content to URL:", error);
      }
    } else {
      userImageResponse.ImageUrl = "";
    }

    return userImageResponse.ImageUrl;
  }

  getUser(userId: number, schoolId?: number): Observable<MiniUserProfile> {
    const schoolQuery = schoolId != null ? `?schoolId=${schoolId}` : '';
    return this.http.get<MiniUserProfile>(this.apiUrl + "/UserProfile/GetMiniProfile/" + userId + schoolQuery);
  }

  getUserFlags(userId: number): Observable<UserImageFlag[]> {
    return this.http.get<UserImageFlag[]>(this.apiUrl + "/UserImage/GetUserImageFlags/" + userId);
  }

  flagUser(userId: number, createTicket: boolean, notes?: string): Observable<CreateFlagResponse> {
    return this.http.post<CreateFlagResponse>(this.apiUrl + "/UserImage/FlagUserImage/", {
      UserId: userId,
      CreateTicket: createTicket,
      Notes: notes
    });
  }

}

export type UserImageFlag = {
  Id: number,
  UserId: number,
  Notes?: string,
  CreatedBy: number,
  CreatedByUserName: string,
  CreatedDateUTC: string
}

/**Response data from flagging a flag*/
export type CreateFlagResponse = {
  /**The id of the new created ticket, if it was created*/
  TicketId?: number,

  /**The Id of the flag*/
  FlagId: number
}

export class UserImageResponse {
  UserId: number = 0;
  ProfileImageContent: any = null; // FileContentResult from backend
  ImageUrl: string = ""; // Converted blob URL for frontend use
  AvatarUrl: string | null = null; // Can be null as per backend
  UserPreferenceUseAvatar: boolean = false;
  HasProfileImage: boolean = true;
  Initials: string = "";
  SecureUrl: string | null = null; // Azure Blob Storage secure URL with SAS token
}

export type MiniUserProfile = {
  Id: number,
  FirstName: string,
  LastName: string,
  Phone: string,
  Email: string,
  Bio: string,
  SchoolName: string,
  Title: string,

  Availability: string,
  OutOfOfficeStartUtc: string,
  OutOfOfficeEndUtc: string,

  AlternativeContactId: number,
  GuardianId: number,
  StudentId: number,

  Students: GuardianStudent[],
  Guardians: GuardianStudent[],
  UserType: string,

  SupervisorFirstName: string,
  SupervisorLastName: string,
  SupervisorUserId: number,
  
  UseAvatar: boolean,
  HasProfilePhoto: boolean,
  ShowInDirectory: boolean,
  CanAccessMessages: boolean,
  /** Soft-deleted user (e.g. former employee): server scrubs contact fields; UI grays out the card */
  IsDeleted: boolean
}
